import { useEffect, useState, useRef } from "react";
import { useChatContext } from "../context/ChatContext";

/**
 * useChat Hook
 * Absorbs complexity of socket listeners for a specific conversation.
 */
export function useChat({ conversationId, onMessage, onTyping, onPresence, onMoodUpdate } = {}) {
    const { socket, connected, joinConversation, sendMessage, sendTyping } = useChatContext();

    // Stable Refs for callbacks
    const onMessageRef = useRef(onMessage);
    const onTypingRef = useRef(onTyping);
    const onPresenceRef = useRef(onPresence);
    const onMoodUpdateRef = useRef(onMoodUpdate);

    useEffect(() => {
        onMessageRef.current = onMessage;
        onTypingRef.current = onTyping;
        onPresenceRef.current = onPresence;
        onMoodUpdateRef.current = onMoodUpdate;
    }, [onMessage, onTyping, onPresence, onMoodUpdate]);

    // Join Room automatically
    useEffect(() => {
        if (connected && conversationId) {
            joinConversation(conversationId);
        }
    }, [connected, conversationId, joinConversation]);

    // Set up Listeners
    useEffect(() => {
        if (!socket) return;

        const handleMsg = (msg) => {
            console.log(`📥 [useChat] Received msg for conv ${msg.conversationId}`, msg);
            // Filter by conversation if needed
            if (conversationId && msg.conversationId !== Number(conversationId)) {
                console.log(`🗑️ [useChat] Ignored msg (Wrong conv: ${msg.conversationId} vs ${conversationId})`);
                return;
            }
            onMessageRef.current?.(msg);
        };

        const handleTyping = (payload) => {
            // Typing usually filtered by UI but good to check
            onTypingRef.current?.(payload);
        };

        const handlePresence = (payload) => {
            onPresenceRef.current?.(payload);
        };

        const handleMoodUpdate = (payload) => {
            onMoodUpdateRef.current?.(payload);
        };

        socket.on("receive_message", handleMsg);
        socket.on("typing", handleTyping);
        socket.on("mood_update", handleMoodUpdate);
        // Presence is global in context, but individual callbacks supported too if needed

        return () => {
            socket.off("receive_message", handleMsg);
            socket.off("typing", handleTyping);
            socket.off("mood_update", handleMoodUpdate);
        };
    }, [socket, conversationId]);

    return {
        connected,
        sendMessage: (payload) => sendMessage({ ...payload, conversationId }), // Auto-inject ID
        setTyping: (isTyping) => sendTyping({ conversationId, isTyping })
    };
}
